import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

function BrandChip({ name, size = 20 }) {
  const initials = (name || '?').slice(0, 2).toUpperCase();
  const hue = (name || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 4,
      background: `hsl(${hue}, 55%, 28%)`,
      border: `1px solid hsl(${hue}, 55%, 40%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.45, fontWeight: 700, color: `hsl(${hue}, 55%, 80%)`,
      flexShrink: 0, letterSpacing: '-0.02em',
    }}>
      {initials}
    </div>
  );
}

/**
 * Reusable brand dropdown used in ThreadSidebar (filter) and New Chat modal.
 */
export default function BrandDropdown({ brands, selectedBrandId, onSelect, placeholder = 'Select brand' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const selected = brands.find(b => b.brand_id === selectedBrandId);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Select brand"
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          padding: '7px 10px',
          background: open ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.04)',
          border: `1px solid ${open ? 'rgba(255,126,103,0.35)' : 'rgba(255,255,255,0.09)'}`,
          borderRadius: 9, cursor: 'pointer',
          transition: 'all 140ms ease', fontFamily: 'inherit',
        }}
      >
        {selected?.logo_url ? (
          <img src={selected.logo_url} alt="" style={{ width: 18, height: 18, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
        ) : selected ? (
          <BrandChip name={selected.brand_name} size={18} />
        ) : (
          <div style={{ width: 18, height: 18, borderRadius: 4, background: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />
        )}
        <span style={{
          flex: 1, textAlign: 'left', fontSize: 13,
          color: selected ? '#FFFFFF' : 'rgba(255,255,255,0.35)',
          fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {selected?.brand_name || placeholder}
        </span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2.5" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 140ms ease', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.12 }}
            role="listbox"
            style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
              background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10, overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
              maxHeight: 220, overflowY: 'auto',
            }}
          >
            {brands.map(b => {
              const isSelected = b.brand_id === selectedBrandId;
              return (
                <div
                  key={b.brand_id}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => { onSelect(b.brand_id); setOpen(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 11px',
                    cursor: 'pointer',
                    background: isSelected ? 'rgba(255,126,103,0.1)' : 'transparent',
                    transition: 'background 100ms ease',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  {b.logo_url ? (
                    <img src={b.logo_url} alt="" style={{ width: 20, height: 20, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                  ) : (
                    <BrandChip name={b.brand_name} size={20} />
                  )}
                  <span style={{
                    fontSize: 13,
                    color: isSelected ? '#FF7E67' : 'rgba(255,255,255,0.75)',
                    fontWeight: isSelected ? 600 : 400,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {b.brand_name}
                  </span>
                  {isSelected && (
                    <svg style={{ marginLeft: 'auto', flexShrink: 0 }} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,126,103,0.8)" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export { BrandChip };
